import { supabase } from "@/integrations/supabase/client";

const NETWORK_ERROR_REGEX = /failed to fetch|networkerror|network request failed|load failed/i;
const RECOVERY_RELOAD_KEY = "auth-network-recovery-reload-at";
const RECOVERY_RELOAD_COOLDOWN_MS = 30_000;

interface PasswordSignInResponse {
  access_token?: string;
  refresh_token?: string;
  error?: string;
  error_description?: string;
  msg?: string;
}

type PasswordFallbackResult =
  | { ok: true }
  | { ok: false; isNetworkError: boolean; errorMessage?: string };

export const isNetworkFetchError = (error: unknown): boolean => {
  if (!error) return false;

  if (typeof error === "string") {
    return NETWORK_ERROR_REGEX.test(error);
  }

  if (error instanceof Error) {
    return NETWORK_ERROR_REGEX.test(error.message);
  }

  if (typeof error === "object" && "message" in error) {
    const message = (error as { message?: unknown }).message;
    return typeof message === "string" && NETWORK_ERROR_REGEX.test(message);
  }

  return false;
};

let activeRecovery: Promise<"recovered" | "reloaded"> | null = null;

const hasRecentRecoveryReload = () => {
  if (typeof window === "undefined") return false;
  const lastReloadAt = Number(window.sessionStorage.getItem(RECOVERY_RELOAD_KEY) ?? 0);
  return Number.isFinite(lastReloadAt) && Date.now() - lastReloadAt < RECOVERY_RELOAD_COOLDOWN_MS;
};

const xhrPasswordSignIn = (url: string, apiKey: string, body: { email: string; password: string }) =>
  new Promise<{ status: number; data: PasswordSignInResponse | null }>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("POST", url, true);
    request.responseType = "text";
    request.timeout = 15_000;
    request.setRequestHeader("Content-Type", "application/json");
    request.setRequestHeader("apikey", apiKey);

    request.onload = () => {
      if (request.status === 0) {
        reject(new Error("Network request failed"));
        return;
      }

      let data: PasswordSignInResponse | null = null;
      if (request.responseText) {
        try {
          data = JSON.parse(request.responseText) as PasswordSignInResponse;
        } catch {
          data = null;
        }
      }

      resolve({ status: request.status, data });
    };

    request.onerror = () => reject(new Error("Network request failed"));
    request.ontimeout = () => reject(new Error("Network request failed"));

    request.send(JSON.stringify(body));
  });

export const attemptPasswordSignInFallback = async (
  email: string,
  password: string
): Promise<PasswordFallbackResult> => {
  if (typeof window === "undefined") {
    return { ok: false, isNetworkError: true };
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const publishableKey =
    import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !publishableKey) {
    return {
      ok: false,
      isNetworkError: false,
      errorMessage: "Authentication is not configured correctly. Please refresh and try again.",
    };
  }

  try {
    const { status, data } = await xhrPasswordSignIn(
      `${supabaseUrl}/auth/v1/token?grant_type=password`,
      publishableKey,
      { email, password }
    );

    if (status >= 400 || !data?.access_token || !data?.refresh_token) {
      return {
        ok: false,
        isNetworkError: false,
        errorMessage:
          data?.error_description ?? data?.msg ?? data?.error ?? "Login failed. Please try again.",
      };
    }

    const { error } = await supabase.auth.setSession({
      access_token: data.access_token,
      refresh_token: data.refresh_token,
    });

    if (error) {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (
        session?.access_token === data.access_token ||
        session?.user?.email?.toLowerCase() === email.toLowerCase()
      ) {
        return { ok: true };
      }

      return {
        ok: false,
        isNetworkError: isNetworkFetchError(error),
        errorMessage: error.message,
      };
    }

    return { ok: true };
  } catch (error) {
    return {
      ok: false,
      isNetworkError: true,
      errorMessage: error instanceof Error ? error.message : "Network request failed",
    };
  }
};

export const recoverAuthNetworkPath = async (): Promise<"recovered" | "reloaded"> => {
  if (typeof window === "undefined") return "recovered";
  if (activeRecovery) return activeRecovery;

  activeRecovery = (async () => {
    try {
      let hadServiceWorkerControl = false;

      if ("serviceWorker" in navigator) {
        hadServiceWorkerControl = Boolean(navigator.serviceWorker.controller);
        const registrations = await navigator.serviceWorker.getRegistrations();
        hadServiceWorkerControl = hadServiceWorkerControl || registrations.length > 0;
        await Promise.all(registrations.map((registration) => registration.unregister()));
      }

      if ("caches" in window) {
        const cacheKeys = await caches.keys();
        const cacheKeysToDelete = cacheKeys.filter(
          (key) =>
            key.includes("workbox") ||
            key.includes("supabase") ||
            key.includes("pwa") ||
            key.includes("vite-plugin-pwa")
        );

        await Promise.all(cacheKeysToDelete.map((key) => caches.delete(key)));
      }

      if (hadServiceWorkerControl && !hasRecentRecoveryReload()) {
        window.sessionStorage.setItem(RECOVERY_RELOAD_KEY, String(Date.now()));
        window.location.reload();
        return "reloaded";
      }

      return "recovered";
    } finally {
      activeRecovery = null;
    }
  })();

  return activeRecovery;
};

export const waitForRetryDelay = (attempt: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, 1000 * (attempt + 1)));
